<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { yyyy } from "./store.svelte";
	import Fa from "svelte-fa";
	import { faCircleNotch, faGlobe, faDrawPolygon, faEraser } from "@fortawesome/free-solid-svg-icons";
	import maplibregl from "maplibre-gl";
	import "maplibre-gl/dist/maplibre-gl.css";
	import bbox from "@turf/bbox";
	import booleanIntersects from "@turf/boolean-intersects";
	import { polygon as turfPolygon } from "@turf/helpers";
	import positron from "./charts/extras/positron.svelte";

	interface Props {
		onComplete: () => void;
		onCancel: () => void;
	}
	let { onComplete, onCancel }: Props = $props();

	const url = yyyy.uri.api;
	const headers = () => ({
		"Content-Type": "application/json",
		Authorization: "Bearer " + yyyy.sesh.logtoken.token,
	});

	const META_CSV =
		"https://raw.githubusercontent.com/wmgeolab/geoBoundaries/b7dd6a55701c76a330500ad9d9240f2b9997c6a8/releaseData/geoBoundariesOpen-meta.csv";

	const ADM_LABELS: Record<string, string> = {
		ADM0: "Admin 0 (Country outline)",
		ADM1: "Admin 1 (States / Provinces)",
		ADM2: "Admin 2 (Districts / Counties)",
		ADM3: "Admin 3",
		ADM4: "Admin 4",
		ADM5: "Admin 5",
	};

	interface LevelEntry {
		adm: string;
		units: number;
		license: string;
	}

	interface CountryEntry {
		iso3: string;
		name: string;
		levels: LevelEntry[];
	}

	let catalog = $state<CountryEntry[]>([]);
	let loadingCatalog = $state(true);
	let selectedIso3 = $state("");
	let selectedAdm = $state("");
	let previewData = $state<any>(null);
	let loadingPreview = $state(false);
	let saving = $state(false);
	let error = $state("");

	// Save form
	let scopeName = $state("");
	let scopeDescription = $state("");

	// Map
	let mapContainer: HTMLDivElement;
	let map: maplibregl.Map | null = null;

	// Drawing state
	let drawing = $state(false);
	let drawPoints = $state<[number, number][]>([]);
	let drawnPolygon = $state<any>(null); // GeoJSON Polygon feature
	let selectedCount = $state<number | null>(null);

	// Derived
	const selectedCountry = $derived(catalog.find((c) => c.iso3 === selectedIso3));
	const availableLevels = $derived(selectedCountry?.levels ?? []);
	const selectedLevel = $derived(availableLevels.find((l) => l.adm === selectedAdm));
	const displayCount = $derived(
		selectedCount !== null
			? selectedCount
			: selectedLevel?.units ?? previewData?.features?.length ?? 0
	);

	const GB_MEDIA_BASE =
		"https://media.githubusercontent.com/media/wmgeolab/geoBoundaries/main/releaseData/gbOpen";

	const simplifiedUrl = (iso3: string, adm: string) =>
		`${GB_MEDIA_BASE}/${iso3}/${adm}/geoBoundaries-${iso3}-${adm}_simplified.geojson`;

	// CSV parser
	const splitCSVLine = (line: string): string[] => {
		const fields: string[] = [];
		let i = 0;
		while (i <= line.length) {
			if (i === line.length) { fields.push(""); break; }
			if (line[i] === '"') {
				let j = i + 1;
				while (j < line.length) {
					if (line[j] === '"') {
						if (j + 1 < line.length && line[j + 1] === '"') { j += 2; }
						else break;
					} else { j++; }
				}
				fields.push(line.substring(i + 1, j));
				i = j + 2;
			} else {
				const j = line.indexOf(",", i);
				if (j === -1) { fields.push(line.substring(i)); break; }
				fields.push(line.substring(i, j));
				i = j + 1;
			}
		}
		return fields;
	};

	const parseCSV = (text: string): CountryEntry[] => {
		const lines = text.split("\n");
		if (lines.length < 2) return [];

		const hdr = splitCSVLine(lines[0]);
		const idx = (name: string) => hdr.indexOf(name);
		const iName = idx("boundaryName");
		const iISO = idx("boundaryISO");
		const iType = idx("boundaryType");
		const iUnits = idx("admUnitCount");
		const iLicense = idx("boundaryLicense");

		if (iISO < 0 || iType < 0) return [];

		const grouped = new Map<
			string,
			{ name: string; levels: Map<string, { units: number; license: string }> }
		>();

		for (let i = 1; i < lines.length; i++) {
			const line = lines[i].trim();
			if (!line) continue;

			const fields = splitCSVLine(line);
			const iso3 = fields[iISO]?.trim();
			const adm = fields[iType]?.trim();
			const name = fields[iName]?.trim() ?? iso3;
			const units = parseInt(fields[iUnits] ?? "0", 10) || 0;
			const license = fields[iLicense]?.trim() ?? "";

			if (!iso3 || !adm) continue;

			if (!grouped.has(iso3)) {
				grouped.set(iso3, { name, levels: new Map() });
			}
			grouped.get(iso3)!.levels.set(adm, { units, license });
		}

		return Array.from(grouped.entries())
			.map(([iso3, v]) => ({
				iso3,
				name: v.name,
				levels: Array.from(v.levels.entries())
					.map(([adm, info]) => ({ adm, ...info }))
					.sort((a, b) => a.adm.localeCompare(b.adm)),
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	};

	const fetchCatalog = async () => {
		loadingCatalog = true;
		try {
			const res = await fetch(META_CSV);
			if (!res.ok) throw new Error(`Failed to fetch catalog (${res.status})`);
			catalog = parseCSV(await res.text());
		} catch (err: any) {
			console.error("Failed to load geoBoundaries catalog:", err);
			error = "Failed to load country catalog";
		} finally {
			loadingCatalog = false;
		}
	};

	const fetchPreview = async () => {
		if (!selectedIso3 || !selectedAdm) return;
		loadingPreview = true;
		error = "";
		previewData = null;
		clearDraw();

		try {
			const geoRes = await fetch(simplifiedUrl(selectedIso3, selectedAdm));
			if (!geoRes.ok) {
				error = "Failed to load preview geometry";
				return;
			}

			previewData = await geoRes.json();

			const levelLabel = selectedAdm === "ADM0" ? "Outline" : selectedAdm;
			scopeName = `${selectedCountry?.name ?? selectedIso3} — ${levelLabel}`;

			renderPreview();
		} catch (err: any) {
			error = err.message;
		} finally {
			loadingPreview = false;
		}
	};

	const initMap = () => {
		if (!mapContainer || map) return;
		map = new maplibregl.Map({
			container: mapContainer,
			style: positron,
			center: [0, 20],
			zoom: 1,
			attributionControl: false,
		});

		map.on("click", onMapClick);
		map.on("dblclick", onMapDblClick);
		map.on("mousemove", onMapMouseMove);
	};

	const renderPreview = () => {
		if (!map || !previewData) return;

		// Remove old layers
		for (const id of ["scope-selected-fill", "scope-fill", "scope-line", "draw-polygon-fill", "draw-polygon-line", "draw-points", "draw-cursor-line"]) {
			if (map.getLayer(id)) map.removeLayer(id);
		}
		for (const id of ["scope-preview", "draw-polygon", "draw-points", "draw-cursor-line"]) {
			if (map.getSource(id)) map.removeSource(id);
		}

		map.addSource("scope-preview", {
			type: "geojson",
			data: previewData,
		});

		map.addLayer({
			id: "scope-fill",
			type: "fill",
			source: "scope-preview",
			paint: {
				"fill-color": "#ff3e00",
				"fill-opacity": 0.15,
			},
		});

		map.addLayer({
			id: "scope-line",
			type: "line",
			source: "scope-preview",
			paint: {
				"line-color": "#ff3e00",
				"line-width": 1.5,
			},
		});

		try {
			const bounds = bbox(previewData) as [number, number, number, number];
			map.fitBounds(bounds, { padding: 40, maxZoom: 10 });
		} catch (e) {
			console.error("Failed to fit bounds:", e);
		}
	};

	// --- Drawing ---

	const startDraw = () => {
		drawing = true;
		drawPoints = [];
		drawnPolygon = null;
		selectedCount = null;
		if (map) map.getCanvas().style.cursor = "crosshair";
		removeDrawLayers();
		removeSelectionHighlight();
	};

	const clearDraw = () => {
		drawing = false;
		drawPoints = [];
		drawnPolygon = null;
		selectedCount = null;
		if (map) map.getCanvas().style.cursor = "";
		removeDrawLayers();
		removeSelectionHighlight();
	};

	const removeDrawLayers = () => {
		if (!map) return;
		for (const id of ["draw-polygon-fill", "draw-polygon-line", "draw-points", "draw-cursor-line"]) {
			if (map.getLayer(id)) map.removeLayer(id);
		}
		for (const id of ["draw-polygon", "draw-points", "draw-cursor-line"]) {
			if (map.getSource(id)) map.removeSource(id);
		}
	};

	const removeSelectionHighlight = () => {
		if (!map) return;
		if (map.getLayer("scope-selected-fill")) map.removeLayer("scope-selected-fill");
		if (map.getSource("scope-selected")) map.removeSource("scope-selected");
	};

	const onMapClick = (e: maplibregl.MapMouseEvent) => {
		if (!drawing || drawnPolygon) return;
		drawPoints = [...drawPoints, [e.lngLat.lng, e.lngLat.lat]];
		updateDrawPreview();
	};

	const onMapDblClick = (e: maplibregl.MapMouseEvent) => {
		if (!drawing || drawnPolygon) return;
		e.preventDefault();

		if (drawPoints.length < 3) return;

		// Close the polygon
		const coords = [...drawPoints, drawPoints[0]];
		drawnPolygon = turfPolygon([coords]);
		drawing = false;
		if (map) map.getCanvas().style.cursor = "";

		updateDrawPreview();
		highlightSelected();
	};

	const onMapMouseMove = (e: maplibregl.MapMouseEvent) => {
		if (!drawing || drawPoints.length === 0 || drawnPolygon) return;
		// Update cursor line from last point to mouse
		if (!map) return;
		const cursorCoords = [...drawPoints, [e.lngLat.lng, e.lngLat.lat]];

		const cursorGeo = {
			type: "Feature" as const,
			properties: {},
			geometry: { type: "LineString" as const, coordinates: cursorCoords },
		};

		if (map.getSource("draw-cursor-line")) {
			(map.getSource("draw-cursor-line") as maplibregl.GeoJSONSource).setData(cursorGeo);
		} else {
			map.addSource("draw-cursor-line", { type: "geojson", data: cursorGeo });
			map.addLayer({
				id: "draw-cursor-line",
				type: "line",
				source: "draw-cursor-line",
				paint: { "line-color": "#3273dc", "line-width": 1.5, "line-dasharray": [3, 3] },
			});
		}
	};

	const updateDrawPreview = () => {
		if (!map) return;

		// Points
		const pointsGeo = {
			type: "FeatureCollection" as const,
			features: drawPoints.map((p) => ({
				type: "Feature" as const,
				properties: {},
				geometry: { type: "Point" as const, coordinates: p },
			})),
		};

		if (map.getSource("draw-points")) {
			(map.getSource("draw-points") as maplibregl.GeoJSONSource).setData(pointsGeo);
		} else {
			map.addSource("draw-points", { type: "geojson", data: pointsGeo });
			map.addLayer({
				id: "draw-points",
				type: "circle",
				source: "draw-points",
				paint: { "circle-radius": 5, "circle-color": "#3273dc", "circle-stroke-width": 2, "circle-stroke-color": "#fff" },
			});
		}

		// Polygon (when closed)
		if (drawnPolygon) {
			if (map.getSource("draw-polygon")) {
				(map.getSource("draw-polygon") as maplibregl.GeoJSONSource).setData(drawnPolygon);
			} else {
				map.addSource("draw-polygon", { type: "geojson", data: drawnPolygon });
				map.addLayer({
					id: "draw-polygon-fill",
					type: "fill",
					source: "draw-polygon",
					paint: { "fill-color": "#3273dc", "fill-opacity": 0.1 },
				});
				map.addLayer({
					id: "draw-polygon-line",
					type: "line",
					source: "draw-polygon",
					paint: { "line-color": "#3273dc", "line-width": 2 },
				});
			}

			// Remove cursor line
			if (map.getLayer("draw-cursor-line")) map.removeLayer("draw-cursor-line");
			if (map.getSource("draw-cursor-line")) map.removeSource("draw-cursor-line");
		}
	};

	const highlightSelected = () => {
		if (!map || !previewData || !drawnPolygon) return;

		const selected = previewData.features.filter((f: any) => {
			try {
				return booleanIntersects(f, drawnPolygon);
			} catch {
				return false;
			}
		});

		selectedCount = selected.length;

		removeSelectionHighlight();

		const selectedGeo = { type: "FeatureCollection" as const, features: selected };

		map.addSource("scope-selected", { type: "geojson", data: selectedGeo });
		map.addLayer({
			id: "scope-selected-fill",
			type: "fill",
			source: "scope-selected",
			paint: { "fill-color": "#3273dc", "fill-opacity": 0.35 },
		});
	};

	const saveScope = async () => {
		if (!scopeName.trim() || !selectedIso3) return;
		saving = true;
		error = "";

		try {
			const body: any = {
				iso3: selectedIso3,
				adm_level: selectedAdm,
				scope_name: scopeName.trim(),
				description: scopeDescription.trim() || null,
			};

			// Pass the drawn polygon as a GeoJSON geometry string for server-side filtering
			if (drawnPolygon) {
				body.boundary = JSON.stringify(drawnPolygon.geometry);
			}

			const response = await fetch(`${url}/rpc/scope_from_geoboundaries`, {
				method: "POST",
				headers: headers(),
				body: JSON.stringify(body),
			});

			if (response.ok) {
				onComplete();
			} else {
				const data = await response.json();
				error = data.message || "Failed to save scope";
			}
		} catch (err: any) {
			error = err.message;
		} finally {
			saving = false;
		}
	};

	// When country changes, auto-select first available ADM level
	$effect(() => {
		if (selectedIso3 && availableLevels.length > 0) {
			const hasAdm1 = availableLevels.some((l) => l.adm === "ADM1");
			selectedAdm = hasAdm1 ? "ADM1" : availableLevels[0].adm;
		}
	});

	// Fetch preview when both are set
	$effect(() => {
		if (selectedIso3 && selectedAdm) {
			fetchPreview();
		}
	});

	onMount(() => {
		fetchCatalog();
		setTimeout(initMap, 100);
	});

	onDestroy(() => {
		if (map) {
			map.remove();
			map = null;
		}
	});
</script>

<div class="scope-library">
	<h3 class="library-title">
		<Fa icon={faGlobe} />
		<span style="margin-left: 0.4rem;">geoBoundaries Library</span>
	</h3>
	<p class="library-hint">Select a country and admin level to add boundaries as a scope</p>

	<div class="library-controls">
		<div class="field">
			<label class="label" for="country-select">Country</label>
			{#if loadingCatalog}
				<div class="loading-inline"><Fa icon={faCircleNotch} spin /> Loading catalog...</div>
			{:else}
				<div class="select is-fullwidth">
					<select id="country-select" bind:value={selectedIso3}>
						<option value="">Select a country...</option>
						{#each catalog as c}
							<option value={c.iso3}>{c.name} ({c.iso3})</option>
						{/each}
					</select>
				</div>
			{/if}
		</div>

		<div class="field">
			<label class="label" for="adm-select">Level</label>
			<div class="select is-fullwidth">
				<select id="adm-select" bind:value={selectedAdm} disabled={!selectedIso3}>
					{#each availableLevels as lvl}
						<option value={lvl.adm}>
							{ADM_LABELS[lvl.adm] ?? lvl.adm} ({lvl.units} units)
						</option>
					{/each}
				</select>
			</div>
		</div>
	</div>

	<div class="preview-map-container">
		{#if loadingPreview}
			<div class="map-loading">
				<Fa icon={faCircleNotch} spin size="1.5x" />
			</div>
		{/if}
		<div class="preview-map" bind:this={mapContainer}></div>
		{#if previewData && !loadingPreview}
			<div class="draw-controls">
				{#if drawnPolygon}
					<button class="draw-btn" onclick={clearDraw} title="Clear selection">
						<Fa icon={faEraser} />
					</button>
				{:else if !drawing}
					<button class="draw-btn" onclick={startDraw} title="Draw polygon to select features">
						<Fa icon={faDrawPolygon} />
					</button>
				{:else}
					<span class="draw-hint">Click to add points, double-click to close</span>
				{/if}
			</div>
		{/if}
	</div>

	{#if selectedLevel}
		<div class="meta-info">
			<span>{selectedCountry?.name ?? selectedIso3}</span>
			<span class="meta-sep">&middot;</span>
			<span>{selectedLevel.units} units</span>
			<span class="meta-sep">&middot;</span>
			<span class="meta-license">{selectedLevel.license || "Open"}</span>
			{#if drawnPolygon && selectedCount !== null}
				<span class="meta-sep">&middot;</span>
				<span class="meta-selected">{selectedCount} selected</span>
			{/if}
		</div>
	{/if}

	{#if previewData && previewData.features?.length > 0}
		<div class="save-form">
			<div class="save-fields">
				<div class="field">
					<label class="label" for="scope-name">Scope Name</label>
					<input
						id="scope-name"
						class="input"
						type="text"
						bind:value={scopeName}
					/>
				</div>
				<div class="field">
					<label class="label" for="scope-desc">Description (optional)</label>
					<input
						id="scope-desc"
						class="input"
						type="text"
						placeholder="e.g. Parish boundaries for reporting"
						bind:value={scopeDescription}
					/>
				</div>
			</div>
			<button
				class="button lvwr-toggle-orange is-fullwidth"
				onclick={saveScope}
				disabled={!scopeName.trim() || saving || (drawnPolygon && selectedCount === 0)}
			>
				{#if saving}
					<Fa icon={faCircleNotch} spin />
					<span style="margin-left: 0.5rem;">Saving...</span>
				{:else}
					Save as Scope ({displayCount} features)
				{/if}
			</button>
		</div>
	{/if}

	{#if error}
		<p class="error-msg">{error}</p>
	{/if}

	<button class="button cancel-btn" onclick={onCancel} disabled={saving}>
		Cancel
	</button>
</div>

<style>
	.scope-library {
		padding: 0.5rem 0;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		height: 100%;
	}

	.library-title {
		font-size: 1rem;
		font-weight: 600;
		color: #363636;
		display: flex;
		align-items: center;
	}

	.library-hint {
		color: #666;
		font-size: 0.85rem;
	}

	.library-controls {
		display: grid;
		grid-template-columns: 2fr 1fr;
		gap: 0.75rem;
	}

	.label {
		font-weight: 600;
		font-size: 0.8rem;
		margin-bottom: 0.25rem;
	}

	.loading-inline {
		color: #999;
		font-size: 0.85rem;
		display: flex;
		align-items: center;
		gap: 0.4rem;
		padding: 0.5rem 0;
	}

	.preview-map-container {
		position: relative;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		overflow: hidden;
		flex: 1;
		min-height: 200px;
	}

	.preview-map {
		width: 100%;
		height: 100%;
		min-height: 200px;
	}

	.map-loading {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		z-index: 10;
		color: #ff3e00;
		background: rgba(255, 255, 255, 0.8);
		border-radius: 50%;
		padding: 0.5rem;
	}

	.draw-controls {
		position: absolute;
		top: 8px;
		right: 8px;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: 0.4rem;
	}

	.draw-btn {
		width: 32px;
		height: 32px;
		border: none;
		border-radius: 4px;
		background: white;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #363636;
		transition: all 0.15s;
	}

	.draw-btn:hover {
		background: #f0f0f0;
		color: #3273dc;
	}

	.draw-hint {
		font-size: 0.75rem;
		background: rgba(255, 255, 255, 0.9);
		padding: 0.25rem 0.5rem;
		border-radius: 4px;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
		color: #3273dc;
		white-space: nowrap;
	}

	.meta-info {
		font-size: 0.8rem;
		color: #666;
		display: flex;
		align-items: center;
		gap: 0.4rem;
	}

	.meta-sep {
		color: #ccc;
	}

	.meta-license {
		color: #3273dc;
		font-weight: 500;
	}

	.meta-selected {
		color: #3273dc;
		font-weight: 600;
	}

	.save-form {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.save-fields {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.75rem;
	}

	.cancel-btn {
		width: 100%;
	}

	.error-msg {
		color: #ff3860;
		font-size: 0.85rem;
	}
</style>
